// IMPORT React
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Toolbar,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  CustomAsyncSelect,
  CustomSelect,
} from "../../components/CustomFields/CustomSelect";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import {
  GetItemDetail,
  CreateItem,
  UpdateItem,
} from "../../services/ItemService";
import { GetDepartmentList } from "../../services/DepartmentService";
import { PreferencesContext } from "../../PreferenceContext";
import CustomBreadcrumb from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import { PAGE_SIZE } from "../../services/common/const";
import { GetSpecialityList } from "../../services/SpecialityService";
import { GetCategoryList } from "../../services/CategoryService";
import { GetUnitList } from "../../services/UnitService";
import { GetTaxList } from "../../services/TaxService";
import {
  getDiscountedValue,
  handleFocus,
  numberFormat,
} from "../../shared/CommonFunction";
import { taxTypes, underModules } from "../../shared/data";
import MedicineGenericNameSave from "../Medicine/MedicineGenericName/MedicineGenericNameSave";
import CategorySave from "../Category/CategorySave";
import UnitSave from "../Unit/UnitSave";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  itemId: string;
  consumerId: string;
  orgId: string;
  branchId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  mode: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  itemId: "",
  consumerId: "",
  orgId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  mode: "DROPDOWN",
};

// SCHEMA FOR FORM
interface formInitialValueTypes {
  itemId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  productTypeCode: string;
  taxTypeCode: selectOption | null;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  unitId: selectOption | null;
  categoryId: selectOption | null;
  taxCode: selectOption | null;
  underModule: Array<selectOption> | null;
  itemName: string;
  unitAmount: number | null;
  discountType: string;
  discountValue: number | null;
  finalAmount: number | null;
  isDeleted: boolean;
}

const inputPropsForNumberField: any = {
  className: "no-spinner",
  inputMode: "numeric",
  pattern: "[0-9]*",
  step: "1",
  min: 0,
  onKeyPress: (event) => {
    if (!/[0-9.]/.test(event.key)) {
      event.preventDefault();
    }
  },
};

export default function ItemSave(Props: any) {
  const {
    isForPopUp = false,
    handleClosePopUp,
    name,
    departmentObject,
    specilattyObject,
    item,
    itemId: propItemId,
  } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initItemData: formInitialValueTypes = {
    itemId: 0,
    consumerId: 0,
    branchId: 0,
    orgId: 0,
    productTypeCode: "SAC",
    taxTypeCode: null,
    departmentId: isForPopUp
      ? departmentObject || null
      : userContext?.departmentId > 0
        ? {
            label: userContext?.departmentName || "",
            value: userContext?.departmentId?.toString(),
          }
        : null,
    specialityId: isForPopUp
      ? specilattyObject || null
      : userContext?.specialityId > 0
        ? {
            label: userContext?.specialityName || "",
            value: userContext?.specialityId?.toString(),
          }
        : null,
    taxCode: null,
    underModule: [{ value: "OPD", label: "OPD" }],
    itemName: isForPopUp ? name?.toUpperCase() : "",
    unitAmount: null,
    discountType: "%",
    discountValue: null,
    finalAmount: null,
    isDeleted: false,
    unitId: null,
    categoryId: null,
  };

  // GETTING PARAMS
  let urlParams = useQuery();
  let curRawItemId = urlParams.get("q");
  let itemId = 0;
  if (propItemId && isForPopUp) {
    itemId = +propItemId;
  } else if (
    curRawItemId !== null &&
    curRawItemId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawItemId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      itemId = +decId;
    }
  } else {
    // if (urlParams.size > 0) {
    //   navigate("/not-found");
    // }
  }

  // STATES FOR DATA STORAGE  -- PLEASE USE PROPER TYPES FOR STATE
  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  const [hsnSacData, setHsnSacData] = useState<any[]>([]);
  const [unitData, setUnitData] = useState<any[]>([]);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [param, setParam] = useState(parameters);

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    itemId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    orgId: Yup.number().required(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    taxCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    unitId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Unit is required"),
    categoryId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Category is required"),
    productTypeCode: Yup.string().required("Product Type is required"),
    itemName: Yup.string().required("Item Name is required"),
    underModule: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.string(),
          label: Yup.string(),
        }),
      )
      .required("Under Module is required"),
    taxTypeCode: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Tax Type is required"),
    unitAmount: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .required("Amount is required"),
    discountType: Yup.string().nullable(),
    discountValue: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable(),
    finalAmount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .nullable()
      .required("Final Amount is required"),
    isDeleted: Yup.boolean(),
  });

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
  });

  const handleClickOpen = (value: boolean, label: string, code: string) => {
    setCustomPopUp({ ...customPopUp, open: value, name: label, code: code });
  };

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const handleManagePopup = async (code, option) => {
    setCustomPopUp({ ...customPopUp, open: false });
    if (code === "UNIT_CREATE") {
      const selectedUnitLabel =
        option?.label || option?.name || customPopUp?.name || "";
      const selectedUnitValue =
        option?.unitId?.toString() || option?.value?.toString() || "";

      methods.setValue(
        "unitId",
        {
          label: selectedUnitLabel,
          value: selectedUnitValue,
        },
        {
          shouldValidate: true,
        },
      );
      setIsLoading(true);
      await fetchUnit("", true);
      setIsLoading(false);
    }
    if (code === "CATEGORY_CREATE") {
      const selectedCategoryLabel =
        option?.label || option?.name || customPopUp?.name || "";
      const selectedCategoryValue =
        option?.categoryId?.toString() || option?.value?.toString() || "";

      methods.setValue(
        "categoryId",
        {
          label: selectedCategoryLabel,
          value: selectedCategoryValue,
        },
        {
          shouldValidate: true,
        },
      );
      setIsLoading(true);
      await fetchCategory("", true);
      setIsLoading(false);
    }
  };

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initItemData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET billing DETAIL
  const getItemDetail = async () => {
    let item: any;
    let params = new URLSearchParams();
    params.append("itemId", itemId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetItemDetail(params);
      item = res.data.data;
      methods.reset({
        ...initItemData,
        itemId: item?.itemId || 0,
        productTypeCode: item?.productTypeCode || "",
        departmentId: {
          value: item?.departmentId || 0,
          label: item?.departmentLabel || "",
        },
        specialityId: {
          value: item?.specialityId || 0,
          label: item?.specialityLabel || "",
        },
        taxCode: item?.taxCode
          ? {
              label: item?.taxCode,
              value: item?.taxCodeLabel?.toString(),
            }
          : null,
        taxTypeCode: item?.taxTypeCode
          ? {
              label: taxTypes?.find((x) => x.value === item?.taxTypeCode)
                ?.label,
              value: item?.taxTypeCode?.toString(),
            }
          : null,
        unitId: {
          value: item?.unitId || 0,
          label: item?.unitLabel || "",
        },
        categoryId: {
          value: item?.categoryId || 0,
          label: item?.categoryLabel || "",
        },
        finalAmount: item?.finalAmount,
        unitAmount: item?.unitAmount,
        discountType: item?.discountType,
        discountValue: item?.discountValue,
        itemName: item?.itemName,
        underModule: item?.underModule
          ? item?.underModule?.split(",")?.map((x) => {
              return { value: x, label: x };
            })
          : [],
      });
      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
        return departmentCollection;
      }
      return [];
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      return [];
    }
  }

  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
        return specialityCollection;
      }
      return [];
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      return [];
    }
  }

  // GET CATEGORY
  async function fetchCategory(searchValue: string, oninit: boolean) {
    try {
      let categoryCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const categoryData: any = await GetCategoryList(params);
      if (categoryData?.data?.data?.categoryColl) {
        categoryCollection = categoryData.data.data.categoryColl;
        if (oninit) setCategoryData(categoryCollection);
      }
      return categoryCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET UNITS
  async function fetchUnit(searchValue: string, oninit: boolean) {
    try {
      let unitCollection = [];
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("mode", "DROPDOWN".toString());
      const unitData: any = await GetUnitList(params);
      if (unitData?.data?.data?.unitColl) {
        unitCollection = unitData.data.data.unitColl;
        if (oninit) setUnitData(unitCollection);
      }
      return unitCollection;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // SAVE AND UPDATE BRANCH
  const saveItem = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        itemId: value.itemId,
        consumerId: userContext?.consumerId,
        orgId: userContext?.organizationId,
        branchId: userContext?.branchId,
        departmentId: value.departmentId?.value,
        specialityId: value.specialityId?.value,
        unitId: value.unitId?.value,
        categoryId: value.categoryId?.value,
        productTypeCode: value.productTypeCode,
        taxTypeCode: value.taxTypeCode?.value,
        itemName: value.itemName?.trim(),
        underModule: value.underModule?.reduce(
          (acc, curr) => (acc += (acc === "" ? "" : ",") + curr.value),
          "",
        ),
        taxCode: value.taxCode?.value,
        unitAmount: value?.unitAmount || 0,
        discountType: value.discountType?.trim(),
        discountValue: value?.discountValue || 0,
        finalAmount: value.finalAmount,
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (itemId > 0) {
        result = await UpdateItem(formData);
      } else {
        result = await CreateItem(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp(
              itemId > 0 ? "ITEM_UPDATE" : "ITEM_CREATE",
              result?.data,
            );
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET HSNSAC
  async function fetchHSNSAC(
    type: string,
    searchValue: string,
    oninit: boolean,
  ) {
    try {
      let taxColl = [];
      let params = new URLSearchParams();
      params.append("taxTypeCode", type.toString());
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue.toString());
      const hsnSacData: any = await GetTaxList(params);
      if (hsnSacData?.data?.data?.taxColl) {
        taxColl = hsnSacData.data.data.taxColl;
        if (oninit) setHsnSacData(taxColl);
      }
      return taxColl;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveItem(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const deptCollection = await fetchDepartment();
      const categoryCollection = await fetchCategory("", true);
      await fetchUnit("", true);
      await fetchHSNSAC(methods.getValues()?.productTypeCode, "", true);
      // Auto-select category if only one option exists (both popup and master page)
      if (categoryCollection?.length === 1) {
        const singleCategory = categoryCollection[0];
        methods.setValue(
          "categoryId",
          { label: singleCategory.label, value: singleCategory.value },
          { shouldValidate: true },
        );
      }
      if (itemId > 0) {
        const item = await getItemDetail();
        await fetchSpeciality(item?.departmentId);
      } else {
        // Auto-select department if only one option exists (popup only)
        if (isForPopUp && !departmentObject && deptCollection?.length === 1) {
          const singleDept = deptCollection[0];
          methods.setValue(
            "departmentId",
            { label: singleDept.label, value: singleDept.value },
            { shouldValidate: true },
          );
          // Fetch specialities for the auto-selected department
          const specCollection = await fetchSpeciality(singleDept.value);
          // Auto-select speciality if only one option exists
          if (!specilattyObject && specCollection?.length === 1) {
            const singleSpec = specCollection[0];
            methods.setValue(
              "specialityId",
              { label: singleSpec.label, value: singleSpec.value },
              { shouldValidate: true },
            );
          }
        } else if (userContext?.departmentId > 0) {
          await fetchSpeciality(userContext?.departmentId?.toString());
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // FILTER HSN/SAC
  async function filterHSN(inputValue: string) {
    let result = await fetchHSNSAC(
      methods.getValues()?.productTypeCode,
      inputValue,
      false,
    );
    return result;
  }

  // FILTER CATEGORY
  async function filterCategory(inputValue: string) {
    let result = await fetchCategory(inputValue, false);
    return result;
  }

  // FILTER UNIT
  async function filterUnit(inputValue: string) {
    let result = await fetchUnit(inputValue, false);
    return result;
  }

  // HSN/SAC LOAD ON SEARCH
  const loadHsnSacList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterHSN(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // CATEGORY LOAD ON SEARCH
  const loadCategoryList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterCategory(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // UNIT LOAD ON SEARCH
  const loadUnitList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterUnit(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [itemId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <Grid container className="page-header">
            <Grid item xs={6} sm={6} md={6}>
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                <CustomBreadcrumb />
              </Box>
            </Grid>
            <Grid item xs={6} sm={6} md={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} mb={3} className="group-container">
                <Grid container item spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.underModule
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Under Module*
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="underModule"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"underModule"}
                              FieldName={"underModule"}
                              options={underModules}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={methods?.getValues()?.itemId > 0}
                              isMulti
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "underModule",
                                  e !== null ? e : [],
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.underModule
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.underModule?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={9} />
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.productTypeCode
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Type *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="productTypeCode"
                          render={({ field }) => {
                            return (
                              <RadioGroup
                                row
                                {...field}
                                onChange={async (e) => {
                                  methods.setValue(
                                    "productTypeCode",
                                    e.target.value,
                                  );
                                  setIsLoading(true);
                                  await fetchHSNSAC(e.target.value, "", true);
                                  methods.setValue("taxCode", null, {
                                    shouldValidate: true,
                                  });
                                  setIsLoading(false);
                                }}
                              >
                                <FormControlLabel
                                  value="HSN"
                                  control={<Radio />}
                                  label="Goods"
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.productTypeCode
                                      ? "validation-message"
                                      : ""
                                  }`}
                                />
                                <FormControlLabel
                                  value="SAC"
                                  control={<Radio />}
                                  label="Services"
                                  className={`fileds-label-text  ${
                                    methods.formState.errors.productTypeCode
                                      ? "validation-message"
                                      : ""
                                  }`}
                                />
                              </RadioGroup>
                            );
                          }}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.productTypeCode?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    sm={6}
                    md={9}
                    display={{
                      xs: "none",
                      sm: "grid",
                    }}
                  />
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={methods?.getValues()?.itemId > 0}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setspecialityData([]);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={methods?.getValues()?.itemId > 0}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "specialityId",
                                  e?.value ? e : null,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.specialityId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  />
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.itemName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Item Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder={
                            methods.getValues("productTypeCode") == "SAC"
                              ? "New Consultation, Old Consultation, Vaccination Charges"
                              : "Rotasil, Influvac Tetra"
                          }
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          id="itemName"
                          size="small"
                          {...methods.register("itemName")}
                          error={!!methods.formState.errors.itemName}
                          className={`text-input ${
                            methods.formState.errors.itemName ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.itemName?.message}
                    </Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.underModule
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Under Module*
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="underModule"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"underModule"}
                              FieldName={"underModule"}
                              options={underModules}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={methods?.getValues()?.itemId > 0}
                              isMulti
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                methods.setValue(
                                  "underModule",
                                  e !== null ? e : []
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.underModule
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.underModule?.message}
                      </Typography>
                    </FormControl>
                  </Grid> */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.unitId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Unit *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="unitId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"unitId"}
                              FieldName={"unitId"}
                              defaultOptions={unitData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadUnitList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (e) {
                                  if (e.__isNew__ === true) {
                                    handleClickOpen(true, e.label, "UNIT");
                                  }
                                }
                                if (
                                  methods
                                    ?.getValues()
                                    ?.unitId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "unitId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.unitId ? "error" : ""
                              }`}
                            />
                          )}
                        />
                        <Typography className="validation-message">
                          {methods.formState.errors.unitId?.message}
                        </Typography>
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.categoryId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Category *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="categoryId"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={false}
                              id={"categoryId"}
                              FieldName={"categoryId"}
                              defaultOptions={categoryData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              loadOptions={loadCategoryList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (e) {
                                  if (e.__isNew__ === true) {
                                    handleClickOpen(true, e.label, "CATEGORY");
                                  }
                                }
                                if (
                                  methods
                                    ?.getValues()
                                    ?.categoryId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "categoryId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.categoryId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                        <Typography className="validation-message">
                          {methods.formState.errors.categoryId?.message}
                        </Typography>
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} />
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.taxTypeCode
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Tax Type *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="taxTypeCode"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"taxTypeCode"}
                              FieldName={"taxTypeCode"}
                              options={taxTypes}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.taxTypeCode?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "taxTypeCode",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  if (e?.value === "TNA") {
                                    methods.setValue("taxCode", null, {
                                      shouldValidate: true,
                                    });
                                  }
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.taxTypeCode
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                        <Typography className="validation-message">
                          {methods.formState.errors.taxTypeCode?.message}
                        </Typography>
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.taxCode
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Tax
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="taxCode"
                          render={({ field }) => (
                            <CustomAsyncSelect
                              {...field}
                              isNotCreatable={true}
                              id={"taxCode"}
                              FieldName={"taxCode"}
                              defaultOptions={hsnSacData}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              isDisabled={
                                methods?.getValues()?.taxTypeCode?.value ===
                                "TNA"
                              }
                              loadOptions={loadHsnSacList}
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={(e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.taxCode?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "taxCode",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.taxCode ? "error" : ""
                              }`}
                            />
                          )}
                        />
                        <Typography className="validation-message">
                          {methods.formState.errors.taxCode?.message}
                        </Typography>
                      </SkeletonWrapper>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  />
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.unitAmount
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Amount *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          type="number"
                          fullWidth
                          id="unitAmount"
                          size="small"
                          inputProps={inputPropsForNumberField}
                          {...methods.register("unitAmount", {
                            onChange(event) {
                              methods.setValue(
                                "unitAmount",
                                event.target.value,
                              );
                              const finalAmount = getDiscountedValue(
                                event.target.value || 0,
                                methods?.getValues()?.discountType,
                                methods?.getValues()?.discountValue || 0,
                              );
                              methods.setValue(
                                "finalAmount",
                                finalAmount || 0,
                                { shouldValidate: true },
                              );
                            },
                          })}
                          error={!!methods.formState.errors.unitAmount}
                          className={`text-input ${
                            methods.formState.errors.unitAmount ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.unitAmount?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.discountValue
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Default Discount
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder="0.00"
                          type="number"
                          fullWidth
                          id="discountValue"
                          size="small"
                          inputProps={inputPropsForNumberField}
                          {...methods.register("discountValue")}
                          onFocus={handleFocus}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Box
                                  sx={{
                                    display: "flex",
                                  }}
                                >
                                  <Box className="input-end-andorment-border">
                                    <Select
                                      {...methods.register("discountType", {
                                        onChange: (e) => {
                                          methods.setValue(
                                            "discountType",
                                            e.target.value,
                                            { shouldValidate: true },
                                          );
                                          const finalAmount =
                                            getDiscountedValue(
                                              methods?.getValues()
                                                ?.unitAmount || 0,
                                              e.target.value,
                                              methods?.getValues()
                                                ?.discountValue || 0,
                                            );
                                          methods.setValue(
                                            "finalAmount",
                                            finalAmount || 0,
                                            { shouldValidate: true },
                                          );
                                        },
                                      })}
                                      value={methods.getValues()?.discountType}
                                      size="small"
                                      displayEmpty
                                      inputProps={{
                                        "aria-label": "Without label",
                                      }}
                                      sx={{
                                        paddingLeft: 1, // Adjust padding as needed
                                        paddingRight: 0,
                                      }}
                                    >
                                      <MenuItem value="%">%</MenuItem>
                                      <MenuItem value="₹">₹</MenuItem>
                                    </Select>
                                  </Box>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            minWidth: "150px",
                            ".MuiOutlinedInput-root": {
                              paddingRight: 0,
                            },
                          }}
                          error={!!methods.formState.errors.discountValue}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.discountValue?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    md={6}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}
                  />
                  <Grid item xs={12} sm={3} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.finalAmount
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Final Amount *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <Typography className="bold-600 pl-5 font-size-30">
                          {numberFormat(methods?.getValues()?.finalAmount || 0)}
                        </Typography>
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.finalAmount?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton || isLoading}
                  >
                    {itemId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "UNIT"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "800px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Unit Name
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <UnitSave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
              popUpUnitId={0}
            />
          </DialogContent>
          <Divider />
        </Dialog>
        <Dialog
          className="dialogPage"
          open={customPopUp.open && customPopUp.code === "CATEGORY"}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "800px",
              height: "400px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                Save Category
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <CategorySave
              handleClosePopUp={(code, company) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
            />
          </DialogContent>
          <Divider />
        </Dialog>
      </Box>
    </Box>
  );
}
